import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useParams, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { OrderProvider } from './context/OrderContext';
import { DataProvider } from './context/DataContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useAnalytics } from './hooks/useAnalytics';

import { OrgProvider, useOrg } from './context/OrgContext';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationProvider } from './context/NotificationContext';
import GlobalNotificationDisplay from './features/notifications/GlobalNotificationDisplay';
import Home from './routes/marketing/Home';
import AboutUs from './routes/marketing/AboutUs';
import Contact from './routes/marketing/Contact';
import ShopDashboard from './routes/cafe/ShopDashboard';
import PublicPage from './routes/cafe/PublicPage';
import ClientCabinet from './routes/organization/ClientCabinet';
import KitchenCabinet from './routes/kitchen/KitchenCabinet';
import MenuManagement from './routes/cafe/MenuManagement';
import TableManagement from './routes/cafe/TableManagement';
import LoginPage from './routes/auth/LoginPage';
import CreateOrganization from './routes/organization/CreateOrganization';
import OrganizationSelector from './routes/organization/OrganizationSelector';
import OrganizationSettings from './routes/organization/OrganizationSettings';
import DemoPage from './routes/demo/DemoPage';
import TableOrderPublic from './routes/cafe/TableOrderPublic';
import AnalyticsDebug from './features/analytics/AnalyticsDebug';
import AnalyticsWrapper from './features/analytics/AnalyticsWrapper';
import PrivacyPolicy from './routes/marketing/PrivacyPolicy';
import TermsOfService from './routes/marketing/TermsOfService';
import CookieConsent from './features/common/CookieConsent';

import './App.css';

const Header: React.FC = () => {
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const { currentOrganization } = useOrg();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  // Check if this is the demo organization
  const isDemoOrg = currentOrganization?.urlName === 'beanbuzz-demo';
  
  const handleLogout = async () => {
    try {
      await logout();
      setMobileMenuOpen(false);
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  if (!currentOrganization) return null;

  const cafeSlug = currentOrganization.urlName;

  return (
    <>
      <header className="header">
        <div className="header-content">
          <Link to={isDemoOrg ? `/${cafeSlug}/public` : `/${cafeSlug}/dashboard`} className="logo">
            <span className="cafe-name">{currentOrganization.name}</span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav>
            <ul className="nav-links">
              {/* For demo org, show public page link by default */}
              {isDemoOrg && (
                <li><Link to={`/${cafeSlug}/public`} className={location.pathname.includes('/public') ? 'active' : ''}>Public Page</Link></li>
              )}
              
              {/* Management links - show for logged-in users or demo orgs */}
              {(isDemoOrg || currentUser) && (
                <>
                  <li><Link to={`/${cafeSlug}/dashboard`} className={location.pathname.includes('/dashboard') ? 'active' : ''}>Dashboard</Link></li>
                  <li><Link to={`/${cafeSlug}/menu`} className={location.pathname.includes('/menu') ? 'active' : ''}>Menu</Link></li>
                  <li><Link to={`/${cafeSlug}/tables`} className={location.pathname.includes('/tables') ? 'active' : ''}>Tables</Link></li>
                  <li><Link to={`/${cafeSlug}/orders`} className={location.pathname.includes('/orders') ? 'active' : ''}>Orders</Link></li>
                  <li><Link to={`/${cafeSlug}/kitchen`} className={location.pathname.includes('/kitchen') ? 'active' : ''}>Kitchen</Link></li>
                </>
              )}
              
              {/* User-specific links */}
              {currentUser && (
                <>
                  <li><Link to={`/${cafeSlug}/settings`} className={location.pathname.includes('/settings') ? 'active' : ''}>⚙️ Settings</Link></li>
                  <li><Link to="/cafes" className="btn btn-ghost">My Cafes</Link></li>
                  <li>
                    <button 
                      onClick={handleLogout}
                      className="btn btn-secondary"
                      style={{ padding: '8px 16px', fontSize: '0.875rem' }}
                    >
                      Logout ({currentUser.email?.split('@')[0]})
                    </button>
                  </li>
                </>
              )}
            </ul>
          </nav>

          {/* Mobile Menu Toggle */}
          <button 
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <div className="hamburger"></div>
          </button>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <div className={`mobile-nav ${mobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}>
        <div className="mobile-nav-content" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-nav-header">
            <h2 className="mobile-nav-title">{currentOrganization.name}</h2>
            <button 
              className="mobile-nav-close"
              onClick={closeMobileMenu}
              aria-label="Close mobile menu"
            >
              <div className="close-icon"></div>
            </button>
          </div>
          
          <ul className="mobile-nav-links">
            {/* For demo org, show public page link by default */}
            {isDemoOrg && (
              <li><Link to={`/${cafeSlug}/public`} onClick={closeMobileMenu} className={location.pathname.includes('/public') ? 'active' : ''}>🌐 Public Page</Link></li>
            )}
            
            {/* Management links - show for logged-in users or demo orgs */}
            {(isDemoOrg || currentUser) && (
              <>
                <li><Link to={`/${cafeSlug}/dashboard`} onClick={closeMobileMenu} className={location.pathname.includes('/dashboard') ? 'active' : ''}>📊 Dashboard</Link></li>
                <li><Link to={`/${cafeSlug}/menu`} onClick={closeMobileMenu} className={location.pathname.includes('/menu') ? 'active' : ''}>📋 Menu</Link></li>
                <li><Link to={`/${cafeSlug}/tables`} onClick={closeMobileMenu} className={location.pathname.includes('/tables') ? 'active' : ''}>🪑 Tables</Link></li>
                <li><Link to={`/${cafeSlug}/orders`} onClick={closeMobileMenu} className={location.pathname.includes('/orders') ? 'active' : ''}>📱 Orders</Link></li>
                <li><Link to={`/${cafeSlug}/kitchen`} onClick={closeMobileMenu} className={location.pathname.includes('/kitchen') ? 'active' : ''}>👨‍🍳 Kitchen</Link></li>
              </>
            )}
            
            {/* User-specific links */}
            {currentUser && (
              <>
                <li><Link to={`/${cafeSlug}/settings`} onClick={closeMobileMenu} className={location.pathname.includes('/settings') ? 'active' : ''}>⚙️ Settings</Link></li>
                <li><Link to="/cafes" onClick={closeMobileMenu}>🏢 My Cafes</Link></li>
                <li>
                  <button 
                    onClick={handleLogout}
                    className="mobile-nav-logout"
                    style={{ 
                      width: '100%', 
                      textAlign: 'left', 
                      background: 'none', 
                      border: 'none', 
                      padding: '12px 16px',
                      color: '#ff3b30',
                      fontWeight: '500',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    🚪 Logout ({currentUser.email?.split('@')[0]})
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </>
  );
};


// Component to handle cafe-specific routing
const OrgRoutes: React.FC = () => {
  const { orgSlug: paramOrgSlug } = useParams<{ orgSlug: string }>();
  const { selectOrganization, setCurrentOrganizationDirectly, currentOrganization, loading: orgLoading } = useOrg();
  const { currentUser } = useAuth();
  const [attemptedLookup, setAttemptedLookup] = React.useState<string | null>(null);

  // Extract orgSlug from URL if not available from params (for demo org route)
  const orgSlug = paramOrgSlug || window.location.pathname.split('/')[1];
  
  // --- DEMO ORG EXCEPTION ---
  const isDemoOrg = orgSlug === 'beanbuzz-demo';

  // Demo organization should work offline - create a demo org object
  const demoOrg = React.useMemo(() => ({
    id: 1,
    name: 'BeanBuzz Demo',
    urlName: 'beanbuzz-demo',
    createdBy: 'demo@beanbuzz.com',
    createdTime: new Date().toISOString(),
    deleted: false
  }), []);

  // Set the demo organization in the context when needed
  React.useEffect(() => {
    if (isDemoOrg && !currentOrganization) {
      setCurrentOrganizationDirectly(demoOrg);
    }
  }, [isDemoOrg, currentOrganization, setCurrentOrganizationDirectly, demoOrg]);

  // For demo org, ensure organization is set immediately
  React.useEffect(() => {
    if (isDemoOrg) {
      setCurrentOrganizationDirectly(demoOrg);
    }
  }, [isDemoOrg, setCurrentOrganizationDirectly, demoOrg]);

  React.useEffect(() => {
    if (orgSlug && (!currentOrganization || currentOrganization.urlName !== orgSlug) && attemptedLookup !== orgSlug) {
      setAttemptedLookup(orgSlug);
      selectOrganization(orgSlug);
    }
  }, [orgSlug, currentOrganization, attemptedLookup, selectOrganization, isDemoOrg]);

  // Reset attempted lookup when orgSlug changes
  React.useEffect(() => {
    setAttemptedLookup(null);
  }, [orgSlug]);

  if (orgLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner-text">Loading cafe...</div>
          <div className="loading-spinner-element"></div>
        </div>
      </div>
    );
  }

  // For demo org, show demo content even if not loaded from API
  if (isDemoOrg && !currentOrganization) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner-text">Loading demo cafe...</div>
          <div className="loading-spinner-element"></div>
        </div>
      </div>
    );
  }

  if (!currentOrganization || currentOrganization.urlName !== orgSlug) {
    return (
      <div className="shop-not-found">
        <h1>Cafe not found</h1>
        <p>The cafe "{orgSlug}" does not exist or you don't have access to it.</p>
        <div className="error-details">
          <p>This could happen if:</p>
          <ul>
            <li>The cafe was deleted</li>
            <li>You don't have permission to access it</li>
            <li>The URL was typed incorrectly</li>
          </ul>
        </div>
        <div className="error-actions">
          <Link to="/" className="btn btn-primary">Back to Home</Link>
          <button 
            onClick={() => window.location.reload()} 
            className="btn btn-secondary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // For demo org, allow access to public pages without authentication
  // For other orgs, require authentication
  if (!isDemoOrg && !currentUser) {
    return <Navigate to="/login" replace />;
  }

  return (
    <DataProvider>
      <OrderProvider>
        <div className="app">
          <Header />
          <main className="main-content">
            <Routes>
              {/* Default route - for demo org, redirect to public page; for others, redirect to dashboard */}
              <Route path="/" element={
                isDemoOrg ? (
                  <Navigate to={`/${orgSlug}/public`} replace />
                ) : (
                  <Navigate to={`/${orgSlug}/dashboard`} replace />
                )
              } />
              {/* Public cafe page - accessible without authentication for demo org */}
              <Route path="/public" element={<PublicPage orgSlug={orgSlug} />} />
              {/* Table ordering - accessible without authentication for demo org */}
              <Route path="/table/*" element={<TableOrderPublic orgSlug={orgSlug} />} />
              {/* Customer ordering - accessible without authentication for demo org */}
              <Route path="/order" element={<ClientCabinet />} />
              {/* Cafe management dashboard */}
              <Route path="/dashboard" element={<ShopDashboard />} />
              {/* Menu management */}
              <Route path="/menu" element={<MenuManagement />} />
              {/* Table management */}
              <Route path="/tables" element={<TableManagement />} />
              {/* Kitchen dashboard */}
              <Route path="/kitchen" element={<KitchenCabinet />} />
              {/* Order management */}
              <Route path="/orders" element={<ClientCabinet />} />
              {/* Cafe settings (owner only) */}
              <Route path="/settings" element={<OrganizationSettings />} />
            </Routes>
          </main>
        </div>
      </OrderProvider>
    </DataProvider>
  );
};

// Component to handle public cafe routes (no authentication required)
const PublicCafeRoutes: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const tableId = pathSegments[pathSegments.length - 1];

  // Handle demo organization specifically
  const isDemoOrg = orgSlug === 'beanbuzz-demo';

  // Determine which component to render based on the current path
  if (location.pathname.includes('/public')) {
    return <PublicPage orgSlug={orgSlug} />;
  } else if (location.pathname.includes('/table')) {
    // Use TableOrderPublic for public table order page
    const hasTableId = pathSegments.length > 3 && pathSegments[3] !== undefined;
    return <TableOrderPublic orgSlug={orgSlug} tableId={hasTableId ? tableId : undefined} />;
  } else if (location.pathname.includes('/order')) {
    return <ClientCabinet />;
  }

  // Default fallback
  return <PublicPage orgSlug={orgSlug} />;
};



const AppContent: React.FC = () => {
  const { currentUser } = useAuth();
  const { hasOrganizations, loading: orgLoading } = useOrg();

  const loading = orgLoading;

  return (
    <Router>
      <AnalyticsWrapper />
      <Routes>
        {/* Landing page for unauthenticated users */}
        <Route 
          path="/" 
          element={
            currentUser ? (
              loading ? (
                <div className="loading-container">
                  <div className="loading-spinner-container">
                    <div className="loading-spinner-text">Checking cafes...</div>
                    <div className="loading-spinner-element"></div>
                  </div>
                </div>
              ) : hasOrganizations ? (
                <Navigate to="/cafes" replace />
              ) : (
                <CreateOrganization />
              )
            ) : (
              <Home />
            )
          } 
        />
        
        {/* About Us page - accessible without authentication */}
        <Route path="/about" element={<AboutUs />} />
        
        {/* Contact page - accessible without authentication */}
        <Route path="/contact" element={<Contact />} />

        {/* Privacy Policy page - accessible without authentication */}
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* Terms of Service page - accessible without authentication */}
        <Route path="/terms" element={<TermsOfService />} />

        {/* Demo page - accessible without authentication */}
        <Route path="/demo" element={<DemoPage />} />
        
        {/* Demo org routes - accessible without authentication (must come before generic org routes) */}
        <Route path="/beanbuzz-demo/*" element={<OrgRoutes />} />
        
        {/* Specific cafe routes - redirect /brew to /brew/public */}
        <Route path="/brew" element={<Navigate to="/brew/public" replace />} />
        
        {/* Protected routes - require authentication */}
        {currentUser ? (
          <>
            {/* Organization selection route */}
            <Route 
              path="/cafes" 
              element={
                loading ? (
                <div className="loading-container">
                  <div className="loading-spinner-container">
                    <div className="loading-spinner-text">Loading cafes...</div>
                    <div className="loading-spinner-element"></div>
                  </div>
                </div>
                ) : hasOrganizations ? (
                  <OrganizationSelector />
                ) : (
                  <CreateOrganization />
                )
              } 
            />

            {/* Legacy routes - redirect to cafes */}
            <Route 
              path="/shops" 
              element={<Navigate to="/cafes" replace />}
            />
            <Route 
              path="/organizations" 
              element={<Navigate to="/cafes" replace />}
            />
            
            {/* Organization-specific routes (excluding demo) */}
            <Route path="/:orgSlug/*" element={<OrgRoutes />} />
          </>
        ) : (
          <>
            {/* Login page for protected routes when not authenticated */}
            <Route path="/cafes" element={<LoginPage />} />
            <Route path="/shops" element={<LoginPage />} />
            <Route path="/organizations" element={<LoginPage />} />
            {/* Redirect non-demo org routes to login */}
            <Route path="/:orgSlug/*" element={<LoginPage />} />
          </>
        )}
        
        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  // Check if the current path is a public route
  const isPublicRoute = () => {
    const path = window.location.pathname;
    // Public routes are: /{orgSlug}/public, /{orgSlug}/table/*, /{orgSlug}/order, /brew
    // But exclude demo org routes which are handled by OrgRoutes
    if (path === '/brew') {
      return true;
    }
    const pathSegments = path.split('/');
    if (pathSegments.length >= 3) {
      const orgSlug = pathSegments[1];
      const routeType = pathSegments[2];
      return orgSlug !== 'beanbuzz-demo' && 
             (routeType === 'public' || routeType === 'table' || routeType === 'order');
    }
    return false;
  };

  // Use public routes for public paths, otherwise use authenticated routes
  if (isPublicRoute()) {
    return <PublicRoutes />;
  }

  return (
    <Provider store={store}>
      <LanguageProvider>
        <NotificationProvider>
          <AuthProvider>
            <OrgProvider>
              <AppContent />
              <GlobalNotificationDisplay />
              <AnalyticsDebug />
              <CookieConsent />
            </OrgProvider>
          </AuthProvider>
        </NotificationProvider>
      </LanguageProvider>
    </Provider>
  );
};

// Standalone public routes component (no context providers)
const PublicRoutes: React.FC = () => {
  return (
    <Provider store={store}>
      <LanguageProvider>
        <NotificationProvider>
          <Router>
            <AnalyticsWrapper />
            <GlobalNotificationDisplay />
            <CookieConsent />
            <Routes>
              {/* Specific cafe routes - redirect /brew to /brew/public */}
              <Route path="/brew" element={<Navigate to="/brew/public" replace />} />
              
              {/* Public routes - accessible without authentication (for non-demo orgs) */}
              <Route path="/:orgSlug/public" element={<PublicCafeRoutes />} />
              <Route path="/:orgSlug/table/*" element={<PublicCafeRoutes />} />
              <Route path="/:orgSlug/order" element={<PublicCafeRoutes />} />
              
              {/* Fallback for public routes */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </NotificationProvider>
      </LanguageProvider>
    </Provider>
  );
};

export default App; 